import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Typography, Table, Button, Space, Modal, Form,
  Input, Row, Col, Tag, Tooltip, Alert, Empty, App, Upload, Radio,
} from 'antd';
import {
  BankOutlined, PlusOutlined, EditOutlined, UploadOutlined, DeleteOutlined, PictureOutlined,
} from '@ant-design/icons';
import BotonBloqueado from '../../components/BotonBloqueado';
import { useAuth } from '../../context/AuthContext';
import PujaSpinner from '../../components/PujaSpinner';
import useColumnasResponsive from '../../components/tabla/useColumnasResponsive';
import { cabanaService, fixUrl } from '../../services/api';

const { Title, Text } = Typography;

/**
 * Las cabañas con las que trabajo: la mía, y las que invité a mis remates.
 *
 * ANTES ESTA PANTALLA LISTABA OTRA COSA. Mostraba filas de la tabla `cabana`,
 * que son ETIQUETAS DE PROCEDENCIA colgadas de mi propio tenant — servían
 * mientras la cabaña consignataria no existía en el sistema. Hoy cada una es su
 * propio cliente de Puja, con panel y con sus animales a su nombre, así que la
 * pantalla pasa a listar TENANTS y el vínculo sale de `evento_invitado`.
 *
 * VUELVE A LISTAR CABAÑAS, y no tenants como en la version anterior. Al
 * reparentar la tabla, cada cabaña paso a ser de su dueño — y un tenant puede
 * tener VARIAS: Juamarita S.A. puede ser "Santa Juana" y "El Sendero". La
 * unidad util volvio a ser la cabaña.
 *
 * HACE UNA SOLA COSA: las unidades productivas. Las invitaciones —que son
 * PERMISOS entre dos empresas, no cabañas— viven en su propia pantalla.
 *
 * Estuvieron un rato aca y fue un error: se invitaba desde esta grilla y se
 * seguia desde Invitaciones, o sea dos lugares para lo mismo. Y peor, una
 * invitacion PENDIENTE no aparecia en ningun lado: esta grilla muestra las
 * cabañas de invitados ACTIVOS, asi que se quedaba sin fila donde mostrarse.
 * Justo la que mas hay que ver.
 *
 * Las ajenas se ven pero no se tocan: las necesito para el catalogo y para
 * marcar de cual viene cada animal consignado. `es_propia` lo decide el
 * backend — comparar ids en el front seria una comparacion duplicada que puede
 * divergir de la que hace el servidor.
 */

const Cabanas = () => {
  const { message } = App.useApp();
  const { tieneRecurso } = useAuth();
  const [cabanas, setCabanas] = useState([]);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cabanaService.getRelacionadas();
      setCabanas(res.data?.cabanas || []);
    } catch (error) {
      message.error(error.response?.data?.error || 'No pudimos cargar las cabañas');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => { cargar(); }, [cargar]);

  const [modalCabana, setModalCabana] = useState(null);   // null | {} | {cabaña}
  const [formCabana] = Form.useForm();
  const [guardandoCabana, setGuardandoCabana] = useState(false);

  const abrirCabana = (cabana) => {
    setModalCabana(cabana || {});
    formCabana.setFieldsValue({
      nom_cabana: cabana?.nom_cabana || '',
      localidad: cabana?.localidad || '',
      tel_contacto: cabana?.tel_contacto || '',
      email_contacto: cabana?.email_contacto || '',
      alias: cabana?.alias || '',
      nombre_publico: cabana?.nombre_publico || '',
    });
  };

  // ── Como se presenta: codigo del CSV y marca publica ───────────────────
  // Los pueden tocar DOS que no pueden editar la cabaña entera: la dueña con
  // plan de solo lectura (Event invitado: ve Cabañas pero no trae
  // `crud_cabanas`) y el organizador que la tiene invitada con la invitacion
  // ACTIVA. Hasta el 2026-10-26 no los podia tocar nadie en un remate
  // compartido y el usuario lo detecto logueado como La Avanzada. La marca
  // publica entro el mismo dia: es lo que ve el comprador y "afecta a todas
  // las partes". El backend repite la regla (`_puede_editar_presentacion`);
  // aca solo se decide que lapiz mostrar.
  const [modalCodigo, setModalCodigo] = useState(null);   // null | fila
  const [formCodigo] = Form.useForm();
  const [guardandoCodigo, setGuardandoCodigo] = useState(false);
  // El LOGO de la cabaña y su fondo en los PDF (2026-10-29). Entran por el
  // mismo modal y las mismas dos puertas que el código y la marca: son la
  // firma del catálogo y las carátulas. Hasta ese día NO había forma de cargar
  // el logo desde el panel (el endpoint existía, roto, y nadie lo llamaba):
  // por eso Monte Grande salía firmado con nombres. `fondo` lo ELIGE el
  // productor; al subir, el backend mide si el logo es claro y sólo lo
  // preselecciona.
  const [logoModal, setLogoModal] = useState({ url: null, fondo: 'ninguno' });
  const [subiendoLogo, setSubiendoLogo] = useState(false);

  const puedeEditarCodigo = (fila) => (fila.es_propia
    ? true
    : tieneRecurso('multi_cabanas') && (fila.invitaciones || []).some((i) => i.activa));

  const abrirCodigo = (fila) => {
    setModalCodigo(fila);
    formCodigo.setFieldsValue({ alias: fila.alias || '', nombre_publico: fila.nombre_publico || '' });
    setLogoModal({ url: fila.logo_cabana || null, fondo: fila.logo_fondo || 'ninguno' });
  };

  const subirLogoModal = async (file) => {
    setSubiendoLogo(true);
    try {
      const res = await cabanaService.uploadLogo(file);
      const claro = res.data?.logo_claro === true;
      setLogoModal((prev) => ({ url: res.data?.logo_cabana, fondo: claro ? 'oscuro' : prev.fondo }));
      if (claro) {
        message.info('El logo es claro: le propusimos la contención para que se vea sobre el papel. Podés cambiarlo.');
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'No pudimos subir el logo');
    } finally {
      setSubiendoLogo(false);
    }
    return false;   // Upload no lo manda solo: lo subimos nosotros
  };

  const guardarCodigo = async () => {
    const valores = await formCodigo.validateFields();
    setGuardandoCodigo(true);
    try {
      const res = await cabanaService.editarPresentacion(modalCodigo.id_cabana, {
        ...valores,
        logo_cabana: logoModal.url || '',
        logo_fondo: logoModal.fondo,
      });
      message.success(`En el CSV ahora se escribe «${res.data?.en_csv}»`);
      setModalCodigo(null);
      cargar();
    } catch (error) {
      message.error(error.response?.data?.error || 'No pudimos guardar cómo aparece la cabaña');
    } finally {
      setGuardandoCodigo(false);
    }
  };

  const guardarCabana = async () => {
    const valores = await formCabana.validateFields();
    setGuardandoCabana(true);
    try {
      if (modalCabana?.id_cabana) {
        await cabanaService.update(modalCabana.id_cabana, valores);
      } else {
        await cabanaService.create(valores);
      }
      message.success(modalCabana?.id_cabana ? 'Cabaña actualizada' : 'Cabaña creada');
      setModalCabana(null);
      cargar();
    } catch (error) {
      message.error(error.response?.data?.error || 'No pudimos guardar la cabaña');
    } finally {
      setGuardandoCabana(false);
    }
  };

  const columnasDesktop = [
    {
      title: 'Cabaña',
      dataIndex: 'nom_cabana',
      key: 'nom_cabana',
      render: (valor, fila) => (
        <Space size={10}>
          {fila.logo_cabana && (
            <img
              src={fixUrl(fila.logo_cabana)}
              alt=""
              style={{
                width: 36, height: 36, objectFit: 'contain', borderRadius: 4, padding: 3,
                // La misma contención que va en el PDF, para que la tabla
                // diga la verdad sobre cómo sale.
                background: fila.logo_fondo === 'oscuro' ? '#333' : 'transparent',
              }}
            />
          )}
          <Space direction="vertical" size={0}>
            <Text strong>{valor}</Text>
            {!fila.es_propia && (
              <Text type="secondary" style={{ fontSize: 12 }}>{fila.duenio}</Text>
            )}
          </Space>
        </Space>
      ),
    },
    {
      // Derivado en el BACKEND, no comparando ids acá: una comparación
      // duplicada en el front es una que puede divergir de la del servidor.
      title: 'Tipo',
      dataIndex: 'es_propia',
      key: 'tipo',
      render: (esPropia) => (esPropia
        ? <Tag color="blue">Propia</Tag>
        : <Tag>Invitada</Tag>),
    },
    {
      // Lo que hay que escribir en la columna CABAÑA del CSV. El alias si la
      // cabaña tiene uno; si no, su nombre — los dos matchean, asi que ninguna
      // queda afuera por no tener alias. Eso era un agujero del modelo anterior.
      title: 'En el CSV escribí',
      dataIndex: 'alias',
      key: 'alias',
      render: (alias, fila) => (
        <Tooltip title={alias ? 'Código corto de esta cabaña' : 'No tiene código corto: se usa el nombre'}>
          <Tag color={alias ? 'blue' : 'default'}>{alias || fila.nom_cabana}</Tag>
        </Tooltip>
      ),
    },
    {
      // Muestra si HAY algo configurado, no el resultado final: ese depende
      // del remate (cuántas cabañas tiene la empresa, quién organiza — ver
      // `Animal.resolver_procedencia()`), y recalcularlo acá sería la misma
      // trampa de siempre: un criterio duplicado que puede divergir del real.
      title: 'Marca pública',
      dataIndex: 'nombre_publico',
      key: 'nombre_publico',
      render: (nombrePublico) => (
        <Tooltip title={nombrePublico
          ? 'Nombre fijo que ve el comprador en el micrositio y el catálogo'
          : 'Se resuelve solo: tu nombre si sos la única cabaña de tu empresa, ' +
            'el de tu empresa si tenés varias'}>
          <Tag color={nombrePublico ? 'blue' : 'default'}>
            {nombrePublico || 'Automático'}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Animales en mis remates',
      dataIndex: 'animales_en_mis_remates',
      key: 'animales',
      align: 'center',
      render: (n) => <Tag color={n > 0 ? 'blue' : 'default'}>{n ?? 0}</Tag>,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      align: 'center',
      width: 100,
      render: (_, fila) => {
        if (fila.es_propia && tieneRecurso('crud_cabanas')) {
          return (
            <Space size={0}>
              <Tooltip title="Editar">
                <Button type="text" icon={<EditOutlined />} onClick={() => abrirCabana(fila)} />
              </Tooltip>
              <Tooltip title="Cómo aparece: logo, código del CSV y marca pública">
                <Button type="text" icon={<PictureOutlined />} onClick={() => abrirCodigo(fila)} />
              </Tooltip>
            </Space>
          );
        }
        /* Sin poder editar la cabaña entera, queda el codigo del CSV: para la
           dueña de solo lectura, y para el organizador sobre las que tiene
           invitadas. El resto de una ajena no se toca: editarla seria
           cambiarle a otra empresa el nombre con el que aparece en su propio
           micrositio. El backend lo rechaza igual. */
        if (puedeEditarCodigo(fila)) {
          return (
            <Tooltip title="Cómo aparece: logo, código del CSV y marca pública">
              <Button type="text" icon={<PictureOutlined />} onClick={() => abrirCodigo(fila)} />
            </Tooltip>
          );
        }
        return fila.es_propia
          // Es tuya, pero tu plan no incluye administrarlas. Motivo `plan`, no
          // `cabana_ajena`: son dos conversaciones distintas.
          ? <BotonBloqueado motivo="plan" type="text" icon={<EditOutlined />} />
          : <BotonBloqueado motivo="cabana_ajena" type="text" icon={<EditOutlined />} />;
      },
    },
  ];

  const { columnas, propsTabla } = useColumnasResponsive({
    desktop: columnasDesktop,
    mobile: {
      title: 'Cabaña',
      // `nom_cabana`, y no `razon_social` (que ni siquiera existe en este
      // payload — lo mas cercano es `duenio`, la razon social del DUEÑO de
      // la cabaña, no la de la fila). Bug preexistente: la key no matcheaba
      // con ninguna columna desktop, asi que `buscarColumna` no la
      // encontraba y el hook caia al fallback de mostrar la tabla completa
      // sin apilar en mobile — justo el patron que existe para evitar.
      principal: 'nom_cabana',
      secundarios: [
        { key: 'tipo' },
        { key: 'alias', etiqueta: 'En el CSV' },
        { key: 'nombre_publico', etiqueta: 'Marca pública' },
        { key: 'animales', etiqueta: 'En mis remates' },
      ],
      conservar: [{ key: 'acciones', width: 60 }],
    },
    scroll: { x: 'max-content' },
  });

  if (loading) return <PujaSpinner />;

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <BankOutlined /> Cabañas
          </Title>
          <Text type="secondary">
            La tuya, y las que invitaste a consignar en tus remates.
          </Text>
        </Col>
        <Col>
          {/* Solo el alta de cabañas PROPIAS. Invitar a otra empresa vive en
              Invitaciones, que es donde se le hace el seguimiento. */}
          {tieneRecurso('crud_cabanas') && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => abrirCabana(null)}>
              Nueva cabaña
            </Button>
          )}
        </Col>
      </Row>

      <Card className="card-tabla">
        {cabanas.length === 0 ? (
          <Empty description="Todavía no hay cabañas para mostrar" />
        ) : (
          <Table
            {...propsTabla}
            columns={columnas}
            dataSource={cabanas}
            // `id_cabana`, no `id_clientes`: el endpoint devuelve una fila
            // POR CABAÑA, y `id_clientes` se REPITE entre las varias cabañas
            // de una misma empresa (Juamarita: "Santa Juana" y "El Sendero").
            // Bug preexistente: con la key duplicada, React confunde la
            // identidad de las filas — el boton Editar de una termina
            // apuntando a la otra.
            rowKey="id_cabana"
            pagination={false}
          />
        )}
      </Card>

      <Modal
        title={modalCabana?.id_cabana ? 'Editar cabaña' : 'Nueva cabaña'}
        open={!!modalCabana}
        onCancel={() => setModalCabana(null)}
        onOk={guardarCabana}
        okText="Guardar"
        okButtonProps={{ loading: guardandoCabana }}
        cancelText="Cancelar"
      >
        <Form form={formCabana} layout="vertical">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Esta es una cabaña TUYA"
            description={
              'Una unidad productiva de tu propia empresa. Si lo que querés es '
              + 'que otra cabaña consigne animales en tu remate, eso se hace con '
              + '"Invitar cabaña".'
            }
          />
          <Form.Item
            name="nom_cabana"
            label="Nombre"
            rules={[{ required: true, message: 'El nombre es requerido' }]}
            extra="Es el nombre que se muestra en el catálogo y en el micrositio."
          >
            <Input placeholder="Santa Juana" maxLength={255} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="localidad" label="Localidad">
                <Input maxLength={255} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="tel_contacto" label="Teléfono">
                <Input maxLength={50} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="email_contacto" label="Email de contacto">
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item
            name="alias"
            label="Código corto para el CSV (opcional)"
            extra={
              'Sólo si el nombre es largo y no querés escribirlo en cada fila. '
              + 'El nombre de la cabaña matchea igual, así que podés dejarlo vacío.'
            }
          >
            <Input placeholder="SJUANA" maxLength={30} />
          </Form.Item>
          <Form.Item
            name="nombre_publico"
            label="Nombre público (opcional)"
            extra={
              'Lo que ve el comprador en el micrositio y el catálogo, si es distinto ' +
              'del nombre de arriba. A diferencia del código del CSV, ESTE campo ' +
              'puede repetirse entre tus cabañas a propósito: si tenés varias y ' +
              'querés que todas se comuniquen bajo la misma marca (ej. "Cabaña ' +
              'Pilagá" en vez de "Cabaña Pilagá Mercedes"), ponéselo igual a todas. ' +
              'Vacío, se resuelve solo: tu nombre si sos la única, el de tu empresa ' +
              'si tenés varias.'
            }
          >
            <Input placeholder="Cabaña Pilagá" maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Cómo aparece esta cabaña"
        open={!!modalCodigo}
        onCancel={() => setModalCodigo(null)}
        onOk={guardarCodigo}
        okText="Guardar"
        okButtonProps={{ loading: guardandoCodigo }}
        cancelText="Cancelar"
      >
        <Form form={formCodigo} layout="vertical">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={modalCodigo?.nom_cabana}
            description={
              (modalCodigo && !modalCodigo.es_propia
                ? 'Es una cabaña invitada: estos datos valen en TODOS los remates '
                  + 'en los que participe, no sólo en el tuyo, y su dueña puede '
                  + 'volver a cambiarlos. '
                : '')
              + 'El resto de la cabaña (nombre, contacto) lo edita sólo su dueña.'
            }
          />

          {/* El logo: la firma de la cabaña en el catálogo y las carátulas
              (esquina de «Notas» en OP1, hoja B de OP2, cartel). Sin logo sale
              el nombre. El preview calca lo que dibuja el PDF: el logo solo
              sobre el papel, o dentro de la contención. */}
          <Form.Item
            label="Logo (opcional)"
            extra={
              'Firma la ficha de cada animal en el catálogo PDF y las carátulas; sin logo sale el nombre. '
              + 'PNG con fondo transparente, apaisado si se puede.'
            }
          >
            <Space align="start" size={16} wrap>
              <div
                style={{
                  width: 190, height: 70, border: '1px dashed #d9d9d9', borderRadius: 6,
                  background: '#fff', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
                  padding: 8,
                }}
              >
                {logoModal.url ? (
                  <div
                    style={{
                      display: 'inline-flex', padding: logoModal.fondo === 'oscuro' ? 5 : 0, borderRadius: 6,
                      background: logoModal.fondo === 'oscuro' ? '#333' : 'transparent',
                    }}
                  >
                    <img
                      src={fixUrl(logoModal.url)}
                      alt=""
                      style={{ maxWidth: 160, maxHeight: 44, objectFit: 'contain', display: 'block' }}
                    />
                  </div>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12, alignSelf: 'center', margin: 'auto' }}>
                    Sin logo: sale el nombre
                  </Text>
                )}
              </div>
              <Space direction="vertical">
                <Upload accept="image/png,image/jpeg,image/gif,image/webp" showUploadList={false} beforeUpload={subirLogoModal}>
                  <Button icon={<UploadOutlined />} loading={subiendoLogo}>
                    {logoModal.url ? 'Cambiar logo' : 'Subir logo'}
                  </Button>
                </Upload>
                {logoModal.url && (
                  <Button
                    icon={<DeleteOutlined />}
                    danger
                    type="text"
                    onClick={() => setLogoModal({ url: null, fondo: 'ninguno' })}
                  >
                    Quitar logo
                  </Button>
                )}
              </Space>
            </Space>
          </Form.Item>
          {logoModal.url && (
            <Form.Item
              label="Fondo del logo en los PDF"
              extra={
                'Un logo blanco sobre el papel no se ve. La contención es una pastilla con el color '
                + 'principal del remate (acá se muestra en gris). Lo elegís vos: al subir un logo claro te lo proponemos.'
              }
            >
              <Radio.Group
                value={logoModal.fondo}
                onChange={(e) => setLogoModal((prev) => ({ ...prev, fondo: e.target.value }))}
              >
                <Radio.Button value="ninguno">Sin fondo</Radio.Button>
                <Radio.Button value="oscuro">Con contención</Radio.Button>
              </Radio.Group>
            </Form.Item>
          )}
          <Form.Item
            name="alias"
            label="En el CSV escribí (opcional)"
            extra="El código corto de la columna CABAÑA de la carga masiva. El nombre completo matchea igual."
          >
            <Input placeholder="AVANZADA" maxLength={30} />
          </Form.Item>
          <Form.Item
            name="nombre_publico"
            label="Marca pública (opcional)"
            extra={
              'Lo que ve el comprador en el micrositio, el catálogo y las carátulas. '
              + 'Vacío, se resuelve solo: el nombre de la cabaña si su empresa tiene una, '
              + 'el de la empresa si tiene varias.'
            }
          >
            <Input placeholder="Cabaña La Avanzada" maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default Cabanas;
